import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Target, ArrowRight, WarningOctagon, CheckCircle, X } from '@phosphor-icons/react';
import { useTenantPath } from '../lib/tenantPath';
import useAuthStore from '../store/authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Yellow reminder banner for the review-commitment loop.
 *
 * Displays when EITHER is true for the current user:
 *   1. They have an ACTIVE commitment window (open now, review upcoming).
 *      → "Commit for Friday's review before 5pm" style CTA.
 *   2. They are a manager with under-commit approvals waiting.
 *      → "3 team commitments awaiting your approval" CTA.
 *
 * Self-hides otherwise. Refetches every 5 minutes so the banner disappears
 * automatically the moment the window closes without a full page reload.
 *
 * Placed in Layout just below the impersonation banner (which is red, this
 * is amber — a clear visual hierarchy: red = critical, amber = todo).
 */
export default function ReviewReminderBanner() {
  const { user } = useAuthStore();
  const nav = useNavigate();
  const tp = useTenantPath();
  const [openCommitments, setOpenCommitments] = useState([]);   // periods needing my commit
  const [pendingApprovals, setPendingApprovals] = useState([]); // commitments awaiting my sign-off
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.impersonated_by) return;  // suppress inside impersonation
    try {
      const [mine, approvals] = await Promise.all([
        axios.get(`${API}/reviews/my-active`),
        // Only managers query approvals — cheap heuristic to avoid 403 noise
        ['Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President']
          .includes(user.role)
          ? axios.get(`${API}/reviews/pending-approvals`)
          : Promise.resolve({ data: { commitments: [] } }),
      ]);
      const active = (mine.data.active_periods || [])
        .filter((r) => r.period.live_status === 'commitment_open' && !r.my_commitment);
      setOpenCommitments(active);
      setPendingApprovals(approvals.data.commitments || []);
    } catch (e) {
      // Silent — feature is optional, don't break Layout on a 4xx
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);  // 5min poll
    return () => clearInterval(t);
  }, [load]);

  if (dismissed) return null;
  // Render-time impersonation guard — matches the fetch-time skip in load().
  // Without this, if a super-admin starts impersonating mid-session, the
  // amber banner keeps rendering with stale data until the 5-min poll ticks.
  if (user?.impersonated_by) return null;
  if (openCommitments.length === 0 && pendingApprovals.length === 0) return null;
  if (!user) return null;

  // Prioritise the user's OWN commit action over their manager duties —
  // your target is more urgent than approving someone else's shortfall.
  const primary =
    openCommitments.length > 0
      ? {
          testid: 'reminder-commit',
          title: openCommitments.length === 1
            ? `Commit for ${openCommitments[0].period.cycle_title} — review ${fmtShortDate(openCommitments[0].period.review_date)}`
            : `${openCommitments.length} review commitments due`,
          sub: openCommitments.length === 1
            ? `Target ₹${(openCommitments[0].my_target || 0).toLocaleString('en-IN')} · window closes ${fmtDateTimeShort(openCommitments[0].period.commitment_closes_at)}`
            : 'Pick your quotes before the review-day cutoff',
          onClick: () => nav(openCommitments.length === 1
            ? tp(`/reviews/commit/${openCommitments[0].period.id}`)
            : tp('/reviews')),
          cta: openCommitments.length === 1 ? 'Commit now' : 'View all',
        }
      : {
          testid: 'reminder-approvals',
          title: `${pendingApprovals.length} under-commit approval${pendingApprovals.length === 1 ? '' : 's'} awaiting you`,
          sub: pendingApprovals.length === 1
            ? `${pendingApprovals[0].user_name} committed ₹${(pendingApprovals[0].committed_value || 0).toLocaleString('en-IN')} against a ₹${(pendingApprovals[0].target_value || 0).toLocaleString('en-IN')} target`
            : 'Review your team\'s below-target commitments',
          onClick: () => nav(pendingApprovals.length === 1
            ? tp(`/reviews/periods/${pendingApprovals[0].period_id}`)
            : tp('/reviews')),
          cta: 'Review now',
        };

  return (
    <div
      role="alert"
      data-testid="review-reminder-banner"
      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md border-b-2 border-amber-700"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-white/20 grid place-items-center shrink-0">
          {openCommitments.length > 0
            ? <Target size={16} weight="fill" />
            : <WarningOctagon size={16} weight="fill" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold leading-tight truncate" data-testid={`${primary.testid}-title`} style={{ fontFamily: 'Outfit, sans-serif' }}>
            {primary.title}
          </div>
          <div className="text-xs opacity-90 truncate">{primary.sub}</div>
        </div>
        <button
          onClick={primary.onClick}
          data-testid={`${primary.testid}-cta`}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-amber-700 font-bold text-xs uppercase tracking-wider hover:bg-amber-50 transition shadow-sm"
        >
          {primary.cta}
          <ArrowRight size={12} weight="bold" />
        </button>
        <button
          onClick={() => setDismissed(true)}
          data-testid="reminder-dismiss"
          aria-label="Dismiss reminder"
          className="shrink-0 w-6 h-6 grid place-items-center rounded hover:bg-white/20 opacity-70 hover:opacity-100"
          title="Hide until next refresh"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

const fmtShortDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
const fmtDateTimeShort = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
